import { Link } from 'react-router-dom';
import { formatRelativeOrDate, Skeleton } from '@ah/design';
import { MediaThumb } from '@ah/design/mobile';
import type { VideoListItem } from '../shared/index.ts';

export function formatDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function formatDurationChip(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function formatTimestamp(seconds: number | null): string | null {
  return formatDurationChip(seconds);
}

export function formatViews(views: number | null): string | null {
  if (!views) return null;
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(0)}K`;
  return String(views);
}

export function VideoRow({ v }: { v: VideoListItem }) {
  const duration = formatDurationChip(v.duration);
  const topic = v.summary?.topics?.[0] ?? null;
  return (
    <Link to={`/videos/${v.id}`} className="block space-y-2">
      <div className="relative">
        <MediaThumb src={v.imageUrl} className="aspect-video rounded-xl" iconSize={24} />
        {duration ? (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[11px] px-1.5 py-0.5 rounded">
            {duration}
          </span>
        ) : null}
      </div>
      <div>
        <p className="text-sm font-semibold leading-snug line-clamp-2">{v.title}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {topic ?? v.channel?.title ?? 'Video'}
          {v.datePublished ? <span> · {formatRelativeOrDate(v.datePublished)}</span> : null}
        </p>
      </div>
    </Link>
  );
}

export function VideoListSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="aspect-video rounded-xl" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function ChannelGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="aspect-square rounded-xl" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function ErrorRetry({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">{label}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}
